import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    IconButton,
    Button,
    Popover,
    PopoverHeader,
    PopoverTrigger,
    PopoverContent,
    Text,
    useMultiStyleConfig,
    PopoverBody,
    Flex,
    SimpleGrid,
    useMediaQuery,
} from '@chakra-ui/react'
import {remCalc} from '../../theme/_utils'
import {AccountIcon} from '../icons/action'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useIntl} from 'react-intl'

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {useGigya} from '../../context/gigya'
import {accountMessages} from '../../pages/account/constant'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {getMrtApiBasepath} from '../../ssr/helpers'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const popoverLinks = [
    {name: 'orderHistory', path: '/orders'},
    {name: 'address', path: '/addresses'},
    {name: 'registeredProducts', path: '/registered-products'},
]

export default function HeaderAccount({setShowLoading}) {
    const {isRegistered} = useCustomerType()

    return isRegistered && isHydrated() ? (
        <LoggedAccountElement setShowLoading={setShowLoading} />
    ) : (
        <GuestAccountElement />
    )
}

function LoggedAccountElement({setShowLoading}) {
    const intl = useIntl()
    const styles = useMultiStyleConfig('Header')

    const [isExpandedHeader] = useMediaQuery('(min-width: 1200px)', {
        ssr: true,
        fallback: false,
    })

    const {gigyaObject} = useGigya()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const navigate = useNavigation()

    const onSignoutClick = async () => {
        setShowLoading(true)
        gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
        await logout.mutateAsync()

        setShowLoading(false)
        const endpoint = origin + getMrtApiBasepath('/logout')
        await fetch(`${endpoint}`).then(() => {
            setTimeout(() => {
                navigate('/login')
            }, 500)
        })
    }

    const {data: customer} = useCurrentCustomer()

    const initials = customer?.firstName?.charAt(0) + customer?.lastName?.charAt(0)

    const headerElement = (
        <Button
            aria-label={intl.formatMessage({
                id: 'header.button.assistive_msg.my_account',
                defaultMessage: 'My Account',
            })}
            sx={styles.headerIcon}
            {...{
                backgroundColor: 'background.dark',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Text
                {...{
                    color: 'text.inverse',
                    fontWeight: 'bold',
                }}
                sx={styles.initialsText}
            >
                {initials ? initials?.toUpperCase() : ''}
            </Text>
        </Button>
    )

    if (!isExpandedHeader) {
        return (
            <Box as={Link} to={`/account`}>
                {headerElement}
            </Box>
        )
    }

    return (
        <Popover isLazy placement="bottom-end" trigger="hover">
            <PopoverTrigger>{headerElement}</PopoverTrigger>
            <PopoverContent w={remCalc(375)} shadow="borderShadow" rounded="none" border="none">
                <PopoverHeader
                    px={remCalc(16)}
                    py={remCalc(24)}
                    borderBottom="small"
                    borderColor="border.light"
                    display="flex"
                >
                    <Text fontWeight="400">
                        {intl.formatMessage({
                            defaultMessage: 'Greetings',
                            id: 'header.popover.text.greetings',
                        })}{' '}
                        <b>{customer?.firstName}</b>
                    </Text>
                </PopoverHeader>
                <PopoverBody
                    borderBottom="small"
                    borderColor="border.light"
                    px={remCalc(16)}
                    py={remCalc(24)}
                >
                    <SimpleGrid gap={remCalc(16)}>
                        <HeaderLinksForLgNotXL />
                        <Button
                            aria-label={intl.formatMessage({
                                defaultMessage: 'My Account',
                                id: 'header.popover.title.my_account',
                            })}
                            as={Link}
                            to={`/account`}
                            textDecor="underline"
                            fontWeight="400"
                        >
                            {intl.formatMessage({
                                defaultMessage: 'My Account',
                                id: 'header.popover.title.my_account',
                            })}
                        </Button>
                        {popoverLinks.map((link) => (
                            <Button
                                as={Link}
                                to={`/account${link.path}`}
                                key={link.name}
                                textDecor="underline"
                                fontWeight="400"
                                aria-label={intl.formatMessage(accountMessages[link.name])}
                            >
                                {intl.formatMessage(accountMessages[link.name])}
                            </Button>
                        ))}
                        <Button
                            aria-label={intl.formatMessage({
                                defaultMessage: 'Log out',
                                id: 'header.popover.action.log_out',
                            })}
                            textAlign="start"
                            textDecor="underline"
                            fontWeight="400"
                            onClick={() => onSignoutClick()}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'Log out',
                                id: 'header.popover.action.log_out',
                            })}
                        </Button>
                    </SimpleGrid>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

function GuestAccountElement() {
    const styles = useMultiStyleConfig('Header')
    const intl = useIntl()

    const [isExpandedHeader] = useMediaQuery('(min-width: 1200px)', {
        ssr: true,
        fallback: false,
    })

    const headerElement = (
        <IconButton
            icon={<AccountIcon sx={styles.accountIcon} />}
            data-testid="account-icon"
            aria-label={intl.formatMessage({
                id: 'header.button.assistive_msg.my_account',
                defaultMessage: 'My Account',
            })}
            variant="unstyled"
            {...styles.buttonIcons}
        />
    )

    if (!isExpandedHeader) {
        return (
            <Box
                as={Link}
                to={`/login`}
                sx={{
                    _focus: {
                        boxShadow: 'outline',
                    },
                }}
            >
                {headerElement}
            </Box>
        )
    }

    return (
        <Popover isLazy placement="bottom-end" trigger="hover">
            <PopoverTrigger>{headerElement}</PopoverTrigger>
            <PopoverContent w={remCalc(375)} shadow="borderShadow" rounded="none" border="none">
                <PopoverBody
                    borderBottom="small"
                    borderColor="border.light"
                    px={remCalc(16)}
                    py={remCalc(24)}
                >
                    <SimpleGrid gap={remCalc(24)}>
                        <Button
                            as={Link}
                            to="/login"
                            variant="primary"
                            aria-label={intl.formatMessage({
                                defaultMessage: 'Login',
                                id: 'header.popover.action.login',
                            })}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'Login',
                                id: 'header.popover.action.login',
                            })}
                        </Button>
                        <Flex gap={remCalc(16)}>
                            <Text>
                                {intl.formatMessage({
                                    defaultMessage: 'New User?',
                                    id: 'header.popover.text.new_user',
                                })}
                            </Text>
                            <Button
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Register now',
                                    id: 'header.popover.action.register_now',
                                })}
                                as={Link}
                                to="/registration"
                                variant="text-button"
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Register now',
                                    id: 'header.popover.action.register_now',
                                })}
                            </Button>
                        </Flex>
                    </SimpleGrid>
                    <Box
                        sx={styles.showOnLgOnly}
                        borderTop="small"
                        borderColor="border.light"
                        mt={remCalc(11)}
                        mx={remCalc(-16)}
                    />
                    <SimpleGrid sx={styles.showOnLgOnly} gap={remCalc(16)} pt={remCalc(16)}>
                        <HeaderLinksForLgNotXL />
                    </SimpleGrid>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

function HeaderLinksForLgNotXL() {
    const styles = useMultiStyleConfig('Header')
    const {data} = useGetMainMenu()
    const headerLinks = data?.headerlinks ? data?.headerlinks[0]?.links : []

    return headerLinks.map((headerLink) => {
        const ctaUrl = headerLink?.link?.href

        const buttonProps = headerLink?.is_external_url
            ? {
                  as: 'a',
                  href: ctaUrl,
                  target: '_blank',
                  rel: 'noopener noreferrer',
              }
            : {
                  as: Link,
                  to: ctaUrl,
              }

        return (
            <Button
                key={headerLink._metadata.uid}
                textDecor="underline"
                fontWeight="400"
                sx={styles.showOnLgOnly}
                aria-label={headerLink?.link?.title}
                {...buttonProps}
            >
                {headerLink?.link?.title}
            </Button>
        )
    })
}

HeaderAccount.propTypes = {
    setShowLoading: PropTypes.func,
}

LoggedAccountElement.propTypes = {
    setShowLoading: PropTypes.func,
}
